import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
  TooltipFormatter,
} from "recharts";

import { IWithballData } from "types";
import { BAR_COLOR } from "MuiProvider/colors";
import { StatisticsBoard } from "components/StatisticsBoard";

import {
  calculatePecentage,
  summariseShoot,
  buildPersonalShootMatrix,
} from "utils";

interface IMyShootPanelProps {
  dataArray: IWithballData[];
  playerId: string;
}

const ASPECT = 2;
const CHART_WIDTH = "80%";
const BAR_SIZE = 20;

export const MyShootPanel = ({ dataArray, playerId }: IMyShootPanelProps) => {
  const { summary, matrix } = useMemo(() => {
    return {
      summary: summariseShoot(dataArray, true),
      matrix: buildPersonalShootMatrix(dataArray),
    };
  }, [dataArray]);

  const tooltipFormatter: TooltipFormatter = (value, name) => {
    const label = intl.get("MATCH_TEAM_OVERVIEW_LAST_PASS_CHART_TITLE");
    return [value, label];
  };

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    const label = intl.get("MATCH_TEAM_OVERVIEW_LAST_PASS_CHART_TITLE");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const renderWhoPassToMeChart = () => {
    if (!matrix.passToMeMatrix) {
      return "No data";
    }

    const data = Object.getOwnPropertyNames(matrix.passToMeMatrix).map(
      (property) => {
        return {
          name:
            property !== "-1" && property !== playerId
              ? matrix.passToMeMatrix[property][0].toName
              : intl.get("MATCH_TEAM_PERSONAL_VIEW_SHOOT_CHART_BY_ME"),
          total: matrix.passToMeMatrix[property].length,
        };
      }
    );

    if (data.length <= 0) {
      return null;
    }

    return (
      <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis dataKey="name" angle={-45} interval={0} />
          <YAxis />
          <Tooltip formatter={tooltipFormatter} />
          <Legend formatter={legendFormatter} />
          <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_TOTAL")}: ${
            summary.all.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.all.inTarget
          } (${calculatePecentage(
            summary.all.inTarget,
            summary.all.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.all.goal} (${calculatePecentage(
              summary.all.goal,
              summary.all.inTarget
            )} / ${calculatePecentage(summary.all.goal, summary.all.total)})`}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_SHOOT_IN_PENALTY_TOTAL"
          )}: ${summary.inPenalty.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.inPenalty.inTarget
          } (${calculatePecentage(
            summary.inPenalty.inTarget,
            summary.inPenalty.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.inPenalty.goal} (${calculatePecentage(
              summary.inPenalty.goal,
              summary.inPenalty.inTarget
            )} / ${calculatePecentage(
              summary.inPenalty.goal,
              summary.inPenalty.total
            )})`}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_SHOOT_OUT_PENALTY_TOTAL"
          )}: ${summary.outPenalty.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_IN_TARGET")}: ${
            summary.outPenalty.inTarget
          } (${calculatePecentage(
            summary.outPenalty.inTarget,
            summary.outPenalty.total
          )})`}</Typography>
          <Typography>
            {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
               ${summary.outPenalty.goal} (${calculatePecentage(
              summary.outPenalty.goal,
              summary.outPenalty.inTarget
            )} / ${calculatePecentage(
              summary.outPenalty.goal,
              summary.outPenalty.total
            )})`}
          </Typography>
        </Grid>
      </Grid>
      {summary.left && (
        <>
          <Box mt={2} />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography>{`${intl.get(
                "MATCH_TEAM_PERSONAL_VIEW_SHOOT_LEFT"
              )}: ${summary.left.total}`}</Typography>
              <Typography>
                {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
                 ${summary.left.goal} (${calculatePecentage(
                  summary.left.goal,
                  summary.left.total
                )})`}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{`${intl.get(
                "MATCH_TEAM_PERSONAL_VIEW_SHOOT_RIGHT"
              )}: ${summary.right.total}`}</Typography>
              <Typography>
                {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
                 ${summary.right.goal} (${
                  (summary.right.goal, summary.right.total)
                })`}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{`${intl.get(
                "MATCH_TEAM_PERSONAL_VIEW_SHOOT_HEADER"
              )}: ${summary.header.total}`}</Typography>
              <Typography>
                {`${intl.get("MATCH_TEAM_OVERVIEW_SHOOT_GOAL")}:
                 ${summary.header.goal} (${calculatePecentage(
                  summary.header.goal,
                  summary.header.total
                )})`}
              </Typography>
            </Grid>
          </Grid>
          <Box mt={2} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="h4">{`${intl.get(
                "MATCH_TEAM_PERSONAL_VIEW_SHOOT_CHART_TITLE"
              )}`}</Typography>
              {renderWhoPassToMeChart()}
            </Grid>
          </Grid>
        </>
      )}
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography variant="h4">{`${intl.get(
            "MATCH_TEAM_PERSONAL_VIEW_SHOOT_SKETCH_MAP_TITLE"
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={dataArray} />
        </Grid>
      </Grid>
    </>
  );
};
